// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fiche d'un prêt social (GET /api/dispositifs/prets/demandes/:id) :
//   demande et termes figés → instruction → décision (montant accordé) → formalités → VERSEMENT PAR LA TRÉSORERIE
//   (paiement PRET préparé ici, puis signé, contresigné et exécuté sur la fiche du paiement : DT-INTEG-005, DM-WEB-006)
//   → créance, solde calculé et échéancier versionné → actes de gestion (proposés par une personne, validés par une
//   autre ; un remboursement anticipé validé est ENCAISSÉ par la trésorerie) → retenues proposées à la Paie (bloquées)
//   → historique avec auteurs. Paiements liés (versement, encaissements) et écriture de démonstration affichés.
// Boutons selon l'état du dossier et les pouvoirs de mes mandats ; le serveur reste juge (refus affichés tels quels).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Banknote, CheckCircle2, ClipboardCheck, FileSignature, Gavel, HandCoins, MoreVertical, Undo2, Wrench, XCircle } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, MenuActions, Squelette } from '../../design/composants';
import { toArray, libelle, aujourdhuiDakar } from './api';
import { useEspaces, droitsDans } from './espaces';
import { Champ, Etat, AccesRefuseDs, ErreurServeur, ListePieces } from './commun';
import {
  useSocial, francs, dateLongue, lireMontant, ETATS_PRET, ETATS_CREANCE, ETATS_PAIEMENT, MODES_PAIEMENT, MODES_PRET, ACTES_PRET, AVIS_INSTRUCTION,
} from './apiSocial';
import {
  useLectureSociale, PageFiche, Infos, TitreSection, HistoriqueActes, FenetreActe, MarqueDemo, Montant, motifValide, ChampMotif, ChoixRadio,
} from './communSocial';
import { ChoixCompteMode, choixInitial, lireChoix, paiementComplet, champsPaiement } from './ChoixPaiement';
import { Echeanciers, RetenuesProposees } from './EcheanciersCommun';
import { LigneDeport } from './Deports';
import { SuiviDossierGestion } from '../../components/suivi/SuiviDemande'; // prochain acteur (lecture)

const STATUTS_DECISION = {
  PROPOSEE: ['alerte', 'Proposée'], ACCORDEE: ['succes', 'Accordée'], ACCORDEE_MODIFIEE: ['succes', 'Accordée avec modification'], REFUSEE: ['neutre', 'Refusée'],
  VALIDEE: ['succes', 'Validée'], REJETEE: ['neutre', 'Rejetée'], ANNULEE: ['neutre', 'Annulée'],
};
const ETAPES_DECISION = { DECIDER: 'Décision d\'octroi', ACTE_GESTION: 'Acte de gestion' };
const STATUTS_DECAISSEMENT = { PREPARE: ['alerte', 'Préparé'], EXECUTE: ['succes', 'Exécuté'], ANNULE: ['neutre', 'Annulé'] };
/** Paiement de trésorerie pas encore exécuté ni annulé. */
const enCours = (p) => p && ['PREPARE', 'SIGNE'].includes(p.statut);
const lienPaiement = (org, p) => `/dispositifs/${org}/tresorerie/paiements/${p.id}`;

function FenetreInstruire({ pret, onFermer, onFait }) {
  const api = useSocial();
  const [avis, setAvis] = useState('RECEVABLE');
  const [motif, setMotif] = useState('');
  const motifExige = avis === 'IRRECEVABLE' || avis === 'COMPLEMENT';
  return (
    <FenetreActe titre="Instruire la demande" Icone={ClipboardCheck} libelleBouton="Enregistrer l'avis" danger={avis === 'IRRECEVABLE'}
      pret={!motifExige || motifValide(motif, 3)} onFermer={onFermer}
      aide="La personne qui a saisi la demande au guichet ne l'instruit pas (double regard)."
      onConfirmer={async () => { await api.instruirePret(pret.id, { avis, ...(motif.trim() ? { motif: motif.trim() } : {}) }); onFait('Avis d\'instruction enregistré.'); }}>
      <ChoixRadio nom="avis" options={AVIS_INSTRUCTION} valeur={avis} onChange={setAvis} />
      <ChampMotif valeur={motif} onChange={setMotif} requis={motifExige} libelleChamp={motifExige ? 'Motif (transmis au salarié)' : 'Observation'} />
    </FenetreActe>
  );
}

function FenetreDecider({ pret, onFermer, onFait }) {
  const api = useSocial();
  const [decision, setDecision] = useState('ACCORDER');
  const [montant, setMontant] = useState(String(pret.montant_demande));
  const [duree, setDuree] = useState(String(pret.nb_echeances_demande));
  const [motif, setMotif] = useState('');
  const m = lireMontant(montant);
  const n = /^\d{1,3}$/.test(duree) ? Number(duree) : null;
  const accorder = decision === 'ACCORDER';
  return (
    <FenetreActe titre="Décider du prêt" Icone={Gavel} libelleBouton={accorder ? 'Accorder' : 'Refuser'} danger={!accorder}
      pret={motifValide(motif, 3) && (!accorder || (m && n))} onFermer={onFermer}
      aide="La décision revient à la fonction prévue par le règlement. Ni la personne qui a saisi, ni celle qui a instruit, ni le bénéficiaire ne décident, même par intérim."
      onConfirmer={async (cle) => {
        await api.deciderPret(pret.id, accorder ? { decision, montant_accorde: m, nb_echeances_accorde: n, motif: motif.trim() } : { decision, motif: motif.trim() }, cle);
        onFait(accorder ? 'Prêt accordé.' : 'Prêt refusé.');
      }}>
      <ChoixRadio nom="decision" options={{ ACCORDER: 'Accorder', REFUSER: 'Refuser' }} valeur={decision} onChange={setDecision} />
      {accorder && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <Champ libelle="Montant accordé (F)" requis aide={`Demandé : ${francs(pret.montant_demande)}`}><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
          <Champ libelle="Durée accordée (mois)" requis aide={`Demandée : ${pret.nb_echeances_demande} mois`}><input className="rh-champ-saisie" inputMode="numeric" value={duree} onChange={(e) => setDuree(e.target.value)} /></Champ>
        </div>
      )}
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins. Conservé dans l'historique." />
    </FenetreActe>
  );
}

function FenetreFormalites({ pret, onFermer, onFait }) {
  const api = useSocial();
  const [mode, setMode] = useState('VIREMENT');
  const [obs, setObs] = useState('');
  return (
    <FenetreActe titre="Formalités du versement" Icone={Wrench} libelleBouton="Enregistrer les formalités" onFermer={onFermer}
      aide={`Montant à verser : ${francs(pret.montant_accorde)}. Le versement est ensuite préparé, signé, contresigné et exécuté à la trésorerie ; la personne qui a décidé l'octroi ne le signe pas.`}
      onConfirmer={async () => { await api.formalitesPret(pret.id, { mode, ...(obs.trim() ? { motif: obs.trim() } : {}) }); onFait('Formalités enregistrées : le prêt est à verser par la trésorerie.'); }}>
      <Champ libelle="Mode de versement prévu" requis>
        <select className="rh-champ-saisie" value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES_PRET.map((x) => <option key={x} value={x}>{MODES_PAIEMENT[x]}</option>)}
        </select>
      </Champ>
      <ChampMotif valeur={obs} onChange={setObs} requis={false} libelleChamp="Observation" aide="Facultative." />
    </FenetreActe>
  );
}

/** Lecture des comptes de trésorerie ACTIFS de l'organisation (choix du compte, du mode et du chèque). */
function useComptesActifs(api, org) {
  const treso = useLectureSociale(() => api.tresorerie(org), [api, org]);
  return { treso, comptes: toArray(treso.donnees?.comptes).filter((c) => c.etat === 'ACTIF') };
}

/** Versement du prêt : préparer le paiement de trésorerie (objet PRET), puis ouvrir sa fiche (signer, contresigner, exécuter). */
function FenetrePaiementPret({ pret, modePrevu, onFermer, onFait }) {
  const api = useSocial();
  const { treso, comptes } = useComptesActifs(api, pret.organisation_id);
  const [choix, setChoix] = useState(choixInitial);
  const [date, setDate] = useState(aujourdhuiDakar());
  const [reference, setReference] = useState('');
  const ch = lireChoix(comptes, choix);
  return (
    <FenetreActe titre="Préparer le paiement du prêt" Icone={Banknote} libelleBouton={`Préparer le paiement de ${francs(pret.montant_accorde)}`}
      pret={!!(paiementComplet(ch) && date)} onFermer={onFermer}
      aide="Le paiement est ensuite signé, contresigné puis exécuté à la trésorerie par d'autres personnes (jamais le bénéficiaire ; la personne qui a décidé l'octroi ne signe pas). La créance et l'échéancier naissent à l'exécution ; les retenues restent seulement proposées (bloquées)."
      onConfirmer={async (cle) => {
        const p = await api.preparerPaiementPret(pret.id, { ...champsPaiement(ch), date_valeur: date, ...(reference.trim() ? { reference: reference.trim() } : {}) }, cle);
        onFait(p);
      }}>
      {treso.erreur && <ErreurServeur erreur={treso.erreur} />}
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>
        Bénéficiaire et montant lus dans le dossier : <Montant n={pret.montant_accorde} fort />{modePrevu ? ` — mode prévu aux formalités : ${libelle(MODES_PAIEMENT, modePrevu).toLowerCase()}` : ''}.
      </p>
      <ChoixCompteMode comptes={comptes} lecture={treso} valeur={choix} onChange={setChoix} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="Date de valeur" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
        <Champ libelle="Référence" aide="Facultative."><input className="rh-champ-saisie" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} /></Champ>
      </div>
    </FenetreActe>
  );
}

/** Remboursement anticipé VALIDÉ : préparer son encaissement (paiement ENCAISSEMENT, objet CREANCE, décision de l'acte). */
function FenetreEncaissement({ pret, acte, onFermer, onFait }) {
  const api = useSocial();
  const { treso, comptes } = useComptesActifs(api, pret.organisation_id);
  const [choix, setChoix] = useState(choixInitial);
  const [date, setDate] = useState(aujourdhuiDakar());
  const [reference, setReference] = useState('');
  const ch = lireChoix(comptes, choix);
  return (
    <FenetreActe titre="Préparer l'encaissement du remboursement anticipé" Icone={HandCoins} libelleBouton={`Préparer l'encaissement de ${francs(acte.montant)}`}
      pret={!!(paiementComplet(ch) && date && acte.creance_id)} onFermer={onFermer}
      aide="L'encaissement est signé, contresigné puis exécuté à la trésorerie. Le remboursement s'applique à l'exécution : nouvelle version de l'échéancier, mouvement sur la créance, écriture de démonstration."
      onConfirmer={async (cle) => {
        const p = await api.preparerPaiement(pret.organisation_id, {
          sens: 'ENCAISSEMENT', objet_type: 'CREANCE', creance_id: acte.creance_id, decision_id: acte.decision_id, montant: acte.montant,
          libelle: `Remboursement anticipé – prêt ${pret.numero}`, date_valeur: date, ...champsPaiement(ch), ...(reference.trim() ? { reference: reference.trim() } : {}),
        }, cle);
        onFait(p);
      }}>
      {treso.erreur && <ErreurServeur erreur={treso.erreur} />}
      <p style={{ margin: '0 0 12px', fontSize: 14 }}>Montant validé : <Montant n={acte.montant} fort />{acte.decide_par ? ` (validé par ${acte.decide_par})` : ''}.</p>
      <ChoixCompteMode comptes={comptes} lecture={treso} valeur={choix} onChange={setChoix} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="Date de valeur" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
        <Champ libelle="Référence" aide="Facultative (virement, reçu de caisse…)."><input className="rh-champ-saisie" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} /></Champ>
      </div>
    </FenetreActe>
  );
}

function FenetreProposerActe({ pret, onFermer, onFait }) {
  const api = useSocial();
  const [type, setType] = useState('SUSPENSION');
  const [nbMois, setNbMois] = useState('1');
  const [montant, setMontant] = useState('');
  const [sens, setSens] = useState('-1');
  const [mode, setMode] = useState('');
  const [motif, setMotif] = useState('');
  const m = lireMontant(montant);
  const nb = /^\d{1,2}$/.test(nbMois) ? Number(nbMois) : null;
  const avecMois = type === 'SUSPENSION' || type === 'REPORT';
  const anticipe = type === 'REMBOURSEMENT_ANTICIPE';
  const pretOk = motifValide(motif) && (!avecMois || nb) && (type !== 'REGULARISATION' || m) && (!anticipe || m);
  return (
    <FenetreActe titre="Proposer un acte de gestion" Icone={Wrench} libelleBouton="Proposer" pret={!!pretOk} onFermer={onFermer}
      aide={anticipe
        ? 'Le remboursement anticipé est proposé ici, validé par une autre personne, puis encaissé par la trésorerie : il ne s\'applique qu\'à l\'exécution de l\'encaissement.'
        : 'L\'acte est proposé ici, puis validé par une autre personne : il ne s\'applique qu\'à la validation (nouvelle version de l\'échéancier).'}
      onConfirmer={async (cle) => {
        let corps = { type, motif: motif.trim() };
        if (avecMois) corps = { ...corps, nb_mois: nb };
        if (type === 'REGULARISATION') corps = { ...corps, sens: Number(sens), montant: m };
        if (anticipe) corps = { ...corps, montant: m, ...(mode ? { mode } : {}) };
        await api.proposerActePret(pret.id, corps, cle);
        onFait('Acte proposé : il attend la validation d\'une autre personne.');
      }}>
      <Champ libelle="Acte" requis>
        <select className="rh-champ-saisie" value={type} onChange={(e) => setType(e.target.value)}>
          {Object.entries(ACTES_PRET).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
      </Champ>
      {avecMois && <Champ libelle="Nombre de mois" requis aide="Entre 1 et 24."><input className="rh-champ-saisie" inputMode="numeric" value={nbMois} onChange={(e) => setNbMois(e.target.value)} /></Champ>}
      {type === 'REGULARISATION' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <Champ libelle="Sens" requis>
            <select className="rh-champ-saisie" value={sens} onChange={(e) => setSens(e.target.value)}>
              <option value="-1">Diminue le restant dû</option><option value="1">Augmente le restant dû</option>
            </select>
          </Champ>
          <Champ libelle="Montant (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
        </div>
      )}
      {anticipe && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <Champ libelle="Montant remboursé (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} /></Champ>
          <Champ libelle="Mode prévu" aide="Facultatif : le mode, la date, la référence et la pièce sont ceux de l'encaissement à la trésorerie.">
            <select className="rh-champ-saisie" value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="">Non précisé</option>
              {MODES_PRET.map((x) => <option key={x} value={x}>{MODES_PAIEMENT[x]}</option>)}
            </select>
          </Champ>
        </div>
      )}
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function FenetreValiderActe({ acte, valider, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`${valider ? 'Valider' : 'Rejeter'} : ${libelle(ACTES_PRET, acte.type).toLowerCase()}`} Icone={valider ? CheckCircle2 : XCircle}
      libelleBouton={valider ? 'Valider' : 'Rejeter'} danger={!valider} pret={motifValide(motif, 3)} onFermer={onFermer}
      aide={`Proposé par ${acte.propose_par || 'une autre personne'}. La personne qui a proposé l'acte ne le valide pas.`}
      onConfirmer={async (cle) => {
        const r = await api.deciderActePret(acte.decision_id, { decision: valider ? 'VALIDER' : 'REJETER', motif: motif.trim() }, cle);
        const aEncaisser = valider && (r?.a_encaisser || acte.type === 'REMBOURSEMENT_ANTICIPE');
        onFait(!valider ? 'Acte rejeté.' : aEncaisser ? 'Remboursement anticipé validé : il attend son encaissement par la trésorerie.' : 'Acte validé et appliqué.');
      }}>
      <ChampMotif valeur={motif} onChange={setMotif} aide="3 caractères au moins." />
    </FenetreActe>
  );
}

function FenetreAnnuler({ pret, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Annuler la demande" Icone={Undo2} libelleBouton="Annuler la demande" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Possible seulement avant le versement, et après l'annulation d'un paiement en cours à la trésorerie. Le salarié en est informé."
      onConfirmer={async () => { await api.annulerPret(pret.id, motif.trim()); onFait('Demande annulée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

const resumeProposition = (p) => {
  if (!p) return '';
  const parts = [];
  if (p.montant_accorde || p.montant_demande) parts.push(francs(p.montant_accorde || p.montant_demande));
  if (p.nb_echeances_accorde || p.nb_echeances_demande) parts.push(`${p.nb_echeances_accorde || p.nb_echeances_demande} mois`);
  if (p.type) parts.unshift(libelle(ACTES_PRET, p.type));
  if (p.nb_mois) parts.push(`${p.nb_mois} mois`);
  if (p.montant && p.type) parts.push(francs(p.montant));
  return parts.join(' · ');
};

function Decisions({ decisions }) {
  const l = toArray(decisions);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucune décision : la proposition de décision naît quand la demande est déclarée recevable.</p>;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Décision</th><th>Proposition</th><th>État</th><th>Proposée par</th><th>Décidée par</th><th>Motif</th></tr></thead>
        <tbody>
          {l.map((d) => (
            <tr key={d.id}>
              <td>{ETAPES_DECISION[d.etape] || d.etape}</td>
              <td>{resumeProposition(d.proposition)}{toArray(d.proposition?.controles).length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12, color: 'var(--sg-texte-2)' }}>
                  {toArray(d.proposition.controles).map((c, i) => <li key={c.code || i}>{c.libelle || c.message || c.code}{c.ok === false ? ' — non satisfait' : ''}</li>)}
                </ul>
              )}</td>
              <td><Etat table={STATUTS_DECISION} code={d.statut} /></td>
              <td>{d.propose_par || <span className="rh-cellule-secondaire">Système</span>}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{dateLongue(d.propose_le)}</span></td>
              <td>{d.decide_par || ''}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{dateLongue(d.decide_le)}</span></td>
              <td>{d.motif_decision || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Decaissements({ decaissements, org }) {
  const l = toArray(decaissements);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun décaissement préparé.</p>;
  return (
    <>
      <div className="rh-conteneur-tableau">
        <table className="rh-tableau">
          <thead><tr><th>Décaissement</th><th style={{ textAlign: 'right' }}>Montant</th><th>Mode</th><th>Date de valeur</th><th>État</th></tr></thead>
          <tbody>
            {l.map((x) => (
              <tr key={x.id}>
                <td>{x.numero || 'Numéro attribué à l\'exécution'}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>préparé le {dateLongue(x.prepare_le)}{x.execute_le ? `, exécuté le ${dateLongue(x.execute_le)}` : ''}</span>
                  {x.paiement_id && <Link className="rh-lien" style={{ display: 'block', fontSize: 13 }} to={lienPaiement(org, { id: x.paiement_id })}>Paiement de trésorerie lié</Link>}</td>
                <td style={{ textAlign: 'right' }}><Montant n={x.montant} /></td>
                <td>{libelle(MODES_PAIEMENT, x.mode)}{x.numero_cheque ? ` n° ${x.numero_cheque}` : ''}{x.reference ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Réf. {x.reference}</span> : null}</td>
                <td>{dateLongue(x.date_valeur)}</td>
                <td><Etat table={STATUTS_DECAISSEMENT} code={x.statut} />{x.motif_annulation ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{x.motif_annulation}</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {l.some((x) => x.piece_id) && <div style={{ marginTop: 12 }}><ListePieces pieces={l.filter((x) => x.piece_id).map((x) => ({ id: x.piece_id, contexte: `Décaissement ${x.numero || ''}`.trim() }))} /></div>}
    </>
  );
}

/** Signataires d'un paiement (noms lus au serveur). */
const signataires = (p) => [
  p.prepare_par_nom && `préparé par ${p.prepare_par_nom}`, p.signe_par_nom && `signé par ${p.signe_par_nom}`,
  p.second_signataire_nom && `contresigné par ${p.second_signataire_nom}`, p.execute_par_nom && `exécuté par ${p.execute_par_nom}`,
].filter(Boolean).join(', ');

/** Paiements de trésorerie liés au prêt : versement (PRET) et encaissements sur sa créance ; écriture de démonstration. */
function PaiementsLies({ paiements, org }) {
  const l = toArray(paiements);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun paiement de trésorerie : le versement se prépare quand les formalités sont faites.</p>;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Paiement</th><th style={{ textAlign: 'right' }}>Montant</th><th>Mode</th><th>État</th><th>Signataires</th><th>Écriture</th></tr></thead>
        <tbody>
          {l.map((p) => (
            <tr key={p.id}>
              <td>
                <Link className="rh-lien" to={lienPaiement(org, p)}>{p.numero || 'Paiement préparé'}</Link>
                <span className="rh-cellule-secondaire" style={{ display: 'block' }}>
                  {p.sens === 'ENCAISSEMENT' ? 'Encaissement (remboursement)' : 'Versement du prêt'}{p.date_valeur ? ` · valeur au ${dateLongue(String(p.date_valeur).slice(0, 10))}` : ''}
                </span>
              </td>
              <td style={{ textAlign: 'right' }}><Montant n={p.montant} /></td>
              <td>{libelle(MODES_PAIEMENT, p.mode)}</td>
              <td><Etat table={ETATS_PAIEMENT} code={p.statut} />{p.statut === 'SIGNE' && !p.second_signataire_nom ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Contresignature attendue</span> : null}</td>
              <td style={{ fontSize: 13 }}>{signataires(p) || <span className="rh-cellule-secondaire">—</span>}</td>
              <td>{p.ecriture_id
                ? <>Passée <Badge ton="neutre">Démonstration</Badge><Link className="rh-lien" style={{ display: 'block', fontSize: 13 }} to={`/dispositifs/${org}/comptabilite`}>Voir la comptabilité</Link></>
                : <span className="rh-cellule-secondaire">{p.statut === 'ANNULE' ? 'Aucune' : 'À l\'exécution'}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Remboursements anticipés validés en attente de leur encaissement par la trésorerie. */
function ActesAEncaisser({ actes, paiements, org, peutPreparer, onEncaisser }) {
  return toArray(actes).map((a) => {
    const p = toArray(paiements).find((x) => x.sens === 'ENCAISSEMENT' && x.decision_id === a.decision_id && enCours(x));
    return (
      <BandeauInfo key={a.decision_id} ton="alerte" titre={`Remboursement anticipé validé : ${francs(a.montant)} à encaisser`}
        action={p
          ? <Link className="rh-lien" to={lienPaiement(org, p)}>Ouvrir l'encaissement</Link>
          : peutPreparer ? <Bouton petit variante="primaire" Icone={HandCoins} onClick={() => onEncaisser(a)}>Préparer l'encaissement</Bouton> : null}>
        {p
          ? `Encaissement en cours à la trésorerie (${libelle(ETATS_PAIEMENT, p.statut).toLowerCase()}) : il s'applique au prêt à son exécution.`
          : `Validé${a.decide_par ? ` par ${a.decide_par}` : ''}. Le remboursement s'applique quand la trésorerie exécute son encaissement.`}
      </BandeauInfo>
    );
  });
}

function ActesGestion({ actes, enAttente, aEncaisser, paiements, org, peutValider, peutPreparer, onDecider, onEncaisser }) {
  const faits = toArray(actes);
  const attente = toArray(enAttente);
  const paiementDe = (a) => toArray(paiements).find((p) => a.decision_id && p.sens === 'ENCAISSEMENT' && p.decision_id === a.decision_id && p.statut === 'EXECUTE');
  if (!faits.length && !attente.length && !toArray(aEncaisser).length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun acte de gestion : remboursement anticipé, suspension, reprise, report ou régularisation se proposent après le versement.</p>;
  return (
    <>
      {attente.map((a) => (
        <BandeauInfo key={a.decision_id} ton="info" titre={`À valider : ${libelle(ACTES_PRET, a.type).toLowerCase()}`}
          action={peutValider ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Bouton petit variante="primaire" Icone={CheckCircle2} onClick={() => onDecider(a, true)}>Valider</Bouton>
              <Bouton petit Icone={XCircle} onClick={() => onDecider(a, false)}>Rejeter</Bouton>
            </div>
          ) : null}>
          {resumeProposition(a.proposition)} — proposé par {a.propose_par || 'une autre personne'}{a.proposition?.motif ? ` : « ${a.proposition.motif} »` : ''}.
        </BandeauInfo>
      ))}
      <ActesAEncaisser actes={aEncaisser} paiements={paiements} org={org} peutPreparer={peutPreparer} onEncaisser={onEncaisser} />
      {faits.length > 0 && (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Date</th><th>Acte</th><th style={{ textAlign: 'right' }}>Montant</th><th>Motif</th></tr></thead>
            <tbody>
              {faits.map((a) => (
                <tr key={a.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateLongue(a.date_operation || a.le)}</td>
                  <td>{libelle(ACTES_PRET, a.type)}{a.mode ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{libelle(MODES_PAIEMENT, a.mode)}{a.reference ? ` · ${a.reference}` : ''}</span> : null}
                    {paiementDe(a) && <Link className="rh-lien" style={{ display: 'block', fontSize: 13 }} to={lienPaiement(org, paiementDe(a))}>Encaissé : paiement {paiementDe(a).numero}</Link>}</td>
                  <td style={{ textAlign: 'right' }}>{a.montant ? <Montant n={a.montant} /> : ''}</td>
                  <td>{a.motif || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function PretPage() {
  const { org, id } = useParams();
  const api = useSocial();
  const naviguer = useNavigate();
  const { espaces } = useEspaces();
  const fiche = useLectureSociale(() => api.pret(id), [api, id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const f = fiche.donnees;
  const d = f?.demande;
  const droits = droitsDans(espaces, d?.organisation_id || org);
  const possibles = toArray(f?.actions_possibles);
  const peut = (action, ...cles) => possibles.includes(action) && cles.some((c) => droits.a(c));
  const fait = (message) => { setFenetre(null); setInfo(message); fiche.recharger(); };
  const decPrepare = toArray(f?.decaissements).find((x) => x.statut === 'PREPARE');
  const orgId = d?.organisation_id || org;
  const versementEnCours = toArray(f?.paiements).find((p) => p.objet_type === 'PRET' && p.sens === 'DECAISSEMENT' && enCours(p));
  /** Paiement préparé : sa fiche s'ouvre (signer, contresigner, exécuter). */
  const ouvrirPaiement = (p) => { setFenetre(null); if (p?.id) naviguer(lienPaiement(orgId, p)); else fait('Paiement préparé.'); };

  const actions = d ? (
    <>
      {peut('INSTRUIRE', 'ds_pret_instruire') && <Bouton variante="primaire" Icone={ClipboardCheck} onClick={() => setFenetre({ type: 'instruire' })}>Instruire</Bouton>}
      {peut('DECIDER', 'ds_pret_decider') && <Bouton variante="primaire" Icone={Gavel} onClick={() => setFenetre({ type: 'decider' })}>Décider</Bouton>}
      {peut('PREPARER_DECAISSEMENT', 'ds_pret_formalites') && <Bouton variante="primaire" Icone={Wrench} onClick={() => setFenetre({ type: 'formalites' })}>Formalités du versement</Bouton>}
      {peut('PREPARER_PAIEMENT', 'ds_paiement_preparer') && !versementEnCours && <Bouton variante="primaire" Icone={Banknote} onClick={() => setFenetre({ type: 'paiement' })}>Préparer le paiement</Bouton>}
      {peut('ACTE_GESTION', 'ds_pret_acte_proposer', 'ds_creance_mouvement_proposer') && <Bouton Icone={FileSignature} onClick={() => setFenetre({ type: 'acte' })}>Proposer un acte</Bouton>}
      {peut('ANNULER', 'ds_pret_instruire') && (
        <MenuActions iconeSeule Icone={MoreVertical} libelle="Autres actions" items={[{ libelle: 'Annuler la demande', Icone: Undo2, danger: true, onClick: () => setFenetre({ type: 'annuler' }) }]} />
      )}
    </>
  ) : null;

  const recap = f?.regles?.recapitulatif;
  return (
    <PageFiche section="prets" libelleSection="Prêts" titre={d ? `Prêt ${d.numero}` : 'Prêt'} actions={actions}>
      {fiche.erreur && (fiche.erreur.statut === 403 || fiche.erreur.statut === 404
        ? <section className="rh-carte"><AccesRefuseDs erreur={fiche.erreur} /></section>
        : <ErreurServeur erreur={fiche.erreur} />)}
      {!f && !fiche.erreur && <Squelette hauteur={160} />}
      {d && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '-8px 0 16px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
            <Etat table={ETATS_PRET} code={d.statut} />
            <span>{f.salarie?.nom}</span><span aria-hidden="true">·</span><span>{f.salarie?.matricule}</span>
            <span aria-hidden="true">·</span><span>{d.dispositif}</span>
            <span aria-hidden="true">·</span><span>{d.canal === 'GUICHET' ? 'Saisie au guichet' : 'Demande du salarié'}</span>
            <MarqueDemo actif={d.donnees_demo} />
          </div>
          <SuiviDossierGestion type="DS_PRET" id={id} cle={fiche.donnees} />
          {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
          {d.motif_statut && ['COMPLEMENT_DEMANDE', 'IRRECEVABLE', 'REFUSEE', 'ANNULEE'].includes(d.statut) && (
            <BandeauInfo ton="info" titre={libelle(ETATS_PRET, d.statut)}>{d.motif_statut}</BandeauInfo>
          )}

          <section className="rh-carte">
            <TitreSection>Demande et termes figés</TitreSection>
            <Infos lignes={[
              ['Montant demandé', <Montant n={d.montant_demande} />], ['Durée demandée', `${d.nb_echeances_demande} mois`],
              ['Montant accordé', d.montant_accorde !== null ? <Montant n={d.montant_accorde} fort /> : null], ['Durée accordée', d.nb_echeances_accorde ? `${d.nb_echeances_accorde} mois` : null],
              ['Déposée le', dateLongue(d.depose_le)], ['Remboursement', d.mode_remboursement === 'RETENUE' ? 'Retenue sur salaire (après accord)' : d.mode_remboursement === 'DIRECT' ? 'Versements directs' : null],
              ['Objet', d.objet_libre],
            ]} />
            {recap && (
              <div style={{ marginTop: 16 }}>
                <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--sg-texte-3)' }}>Récapitulatif présenté au dépôt (règles figées à cette date)</p>
                {recap.phrase && <p style={{ margin: '0 0 4px', fontWeight: 500 }}>{recap.phrase}</p>}
                <p style={{ margin: 0, fontSize: 14, color: 'var(--sg-texte-2)' }}>{toArray(recap.lignes).map((l) => `${l.libelle} : ${l.valeur}`).join(' · ')}</p>
              </div>
            )}
            {f.regles?.empreinte && <p className="rh-form-aide" style={{ margin: '10px 0 0' }} title={f.regles.empreinte}>Règlement figé, empreinte {String(f.regles.empreinte).slice(0, 12)}…</p>}
          </section>

          <section className="rh-carte">
            <TitreSection>Instruction et décision</TitreSection>
            <Decisions decisions={toArray(f.decisions).filter((x) => x.etape !== 'ACTE_GESTION')} />
          </section>

          <section className="rh-carte">
            <TitreSection>Versement et encaissements (trésorerie) <Badge ton="neutre">Comptabilité : démonstration</Badge></TitreSection>
            {versementEnCours && (
              <BandeauInfo ton="info" titre="Versement en cours à la trésorerie" action={<Link className="rh-lien" to={lienPaiement(orgId, versementEnCours)}>Ouvrir la fiche du paiement</Link>}>
                Paiement {libelle(ETATS_PAIEMENT, versementEnCours.statut).toLowerCase()} : il reste à le {versementEnCours.statut === 'PREPARE' ? 'signer, le contresigner puis l\'exécuter' : versementEnCours.second_signataire_nom ? 'exécuter' : 'contresigner puis l\'exécuter'}. La créance naît à l'exécution.
              </BandeauInfo>
            )}
            <PaiementsLies paiements={f.paiements} org={orgId} />
            {toArray(f.decaissements).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--sg-texte-3)' }}>Décaissement au dossier du prêt</p>
                <Decaissements decaissements={f.decaissements} org={orgId} />
              </div>
            )}
          </section>

          <section className="rh-carte">
            <TitreSection>Créance et échéancier</TitreSection>
            {f.creance ? (
              <div style={{ marginBottom: 16 }}>
                <Infos lignes={[
                  ['Montant initial', <Montant n={f.creance.montant_initial} />], ['Restant dû', <Montant n={f.creance.solde} fort />],
                  ['État', <Etat table={ETATS_CREANCE} code={f.creance.etat} />],
                ]} />
              </div>
            ) : <p className="rh-cellule-secondaire" style={{ margin: '0 0 8px' }}>La créance naît au décaissement.</p>}
            <Echeanciers versions={f.echeanciers} />
          </section>

          <section className="rh-carte">
            <TitreSection>Actes de gestion</TitreSection>
            <ActesGestion actes={f.actes} enAttente={f.actes_en_attente} aEncaisser={f.actes_a_encaisser} paiements={f.paiements} org={orgId}
              peutValider={droits.a('ds_pret_acte_valider') || droits.a('ds_creance_mouvement_valider')} peutPreparer={droits.a('ds_paiement_preparer')}
              onDecider={(a, valider) => setFenetre({ type: 'valider', acte: a, valider })} onEncaisser={(a) => setFenetre({ type: 'encaisser', acte: a })} />
          </section>

          <section className="rh-carte">
            <TitreSection>Retenues proposées <Badge ton="neutre">Paie : simulation</Badge></TitreSection>
            <RetenuesProposees retenues={f.retenues} />
          </section>

          <section className="rh-carte">
            <TitreSection>Historique</TitreSection>
            <HistoriqueActes lignes={f.historique} etats={ETATS_PRET} />
          </section>
        </>
      )}

      {fenetre?.type === 'instruire' && <FenetreInstruire pret={d} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'decider' && <FenetreDecider pret={d} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'formalites' && <FenetreFormalites pret={d} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'paiement' && <FenetrePaiementPret pret={d} modePrevu={decPrepare?.mode} onFermer={() => setFenetre(null)} onFait={ouvrirPaiement} />}
      {fenetre?.type === 'encaisser' && <FenetreEncaissement pret={d} acte={fenetre.acte} onFermer={() => setFenetre(null)} onFait={ouvrirPaiement} />}
      {fenetre?.type === 'acte' && <FenetreProposerActe pret={d} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'valider' && <FenetreValiderActe acte={fenetre.acte} valider={fenetre.valider} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'annuler' && <FenetreAnnuler pret={d} onFermer={() => setFenetre(null)} onFait={fait} />}
      {d && <LigneDeport organisationId={orgId} salarie={{ id: d.employe_id, nom: f.salarie?.nom, matricule: f.salarie?.matricule }} droits={droits} />}
    </PageFiche>
  );
}

